// The generic `request()` wrapper in src/api/hooks.ts rejects with
// `new Error(JSON.stringify(problemDetail))`, so every caller that wants to
// branch on the backend's error *code* has to dig the payload back out of the
// message string. These helpers do that once, safely.

/** Shape of the backend's error payload (internal/handlers/base.ErrorResponse). */
export interface ApiErrorPayload {
    title?: string
    code?: string
    fields?: { name: string; message: string }[]
}

/**
 * parseApiError recovers the backend problem detail from a thrown error.
 * Returns null when the error is not a serialized API payload (network
 * failures, programming errors, …).
 */
export function parseApiError(error: unknown): ApiErrorPayload | null {
    if (!error) return null

    // Some call sites hand us the payload object directly.
    if (typeof error === 'object' && error !== null && !(error instanceof Error)) {
        const candidate = error as ApiErrorPayload
        if (typeof candidate.code === 'string' || typeof candidate.title === 'string') {
            return candidate
        }
    }

    const message =
        error instanceof Error
            ? error.message
            : typeof error === 'string'
              ? error
              : null
    if (!message) return null

    try {
        const parsed = JSON.parse(message)
        if (parsed && typeof parsed === 'object') return parsed as ApiErrorPayload
    } catch {
        // Not JSON — a plain Error message.
    }
    return null
}

/** apiErrorCode returns the backend error code, or null when there is none. */
export function apiErrorCode(error: unknown): string | null {
    return parseApiError(error)?.code ?? null
}

/**
 * apiErrorMessage returns something worth showing a user: the payload's title
 * when there is one, otherwise the raw error message. Never the JSON blob.
 */
export function apiErrorMessage(error: unknown, fallback: string): string {
    const payload = parseApiError(error)
    if (payload?.title) return payload.title
    if (error instanceof Error && error.message && !error.message.startsWith('{')) {
        return error.message
    }
    return fallback
}

/**
 * apiErrorDetail is apiErrorMessage followed by the per-field messages of a
 * validation error, so the user sees why a value was refused.
 */
export function apiErrorDetail(error: unknown, fallback: string): string {
    const message = apiErrorMessage(error, fallback)
    const details = (parseApiError(error)?.fields ?? []).map((f) => f.message).filter(Boolean)
    return details.length ? `${message}: ${details.join('; ')}` : message
}
